/**
 * Pure geometry of the assembly marks (no DOM, no assets) — see
 * `sheet-marks.ts` for the drawing and the full rationale.
 */

export interface PageSheetMarks {
  /** Full width of the shared strip, in millimetres. */
  overlapMm: number;
  /**
   * Millimetres cut off this sheet's left/top edge. The remaining
   * `overlapMm - trimMm` is what the upper sheet actually covers, i.e. the
   * glue area on its neighbour.
   */
  trimMm: number;
  /** Trim this edge — the sheet has a neighbour to its left. */
  cutLeft: boolean;
  /** Trim this edge — the sheet has a neighbour above. */
  cutTop: boolean;
  /** Glue area — a neighbour is laid on top from the right. */
  glueRight: boolean;
  /** Glue area — a neighbour is laid on top from below. */
  glueBottom: boolean;
}

/**
 * Marks for the sheet at (`col`, `row`) of a `cols` × `rows` grid. Each seam is
 * trimmed on exactly one of its two sheets and glued on the other: sheets are
 * laid left-to-right, top-to-bottom, so a sheet is cut where a neighbour
 * precedes it and glued where one follows.
 */
export function sheetMarksFor(args: {
  col: number;
  row: number;
  cols: number;
  rows: number;
  overlapMm: number;
  trimMm: number;
}): PageSheetMarks {
  return {
    overlapMm: args.overlapMm,
    trimMm: args.trimMm,
    cutLeft: args.col > 0,
    cutTop: args.row > 0,
    glueRight: args.col < args.cols - 1,
    glueBottom: args.row < args.rows - 1,
  };
}

export interface MarkRect {
  xMm: number;
  yMm: number;
  wMm: number;
  hMm: number;
}

export interface MarkLine {
  x1Mm: number;
  y1Mm: number;
  x2Mm: number;
  y2Mm: number;
}

/** A glyph centred on (cxMm, cyMm); wMm/hMm are its unrotated size. */
export interface MarkGlyph {
  cxMm: number;
  cyMm: number;
  wMm: number;
  hMm: number;
  /** Turned a quarter turn so it runs along a vertical edge. */
  vertical: boolean;
}

/**
 * Renderer-neutral description of the assembly marks, in millimetres with y
 * pointing down and the origin at the sheet's top-left corner.
 */
export interface SheetMarkGeometry {
  /** Non-overlapping rectangles, so an opacity fill has no darker corner. */
  glueTint: MarkRect[];
  glueLines: MarkLine[];
  glueGlyphs: MarkGlyph[];
  cutLines: MarkLine[];
  scissors: MarkGlyph[];
}

/** Scissors glyph native size, in its own path units. */
export const SCISSORS_VIEW_W = 1280;
export const SCISSORS_VIEW_H = 753;

/**
 * Assumed unprintable border, in millimetres. Only used to keep marks inside
 * the area a printer will actually put ink on — a mark drawn nearer the edge
 * than this is simply never seen. Deliberately conservative: overshooting
 * costs a slightly narrower mark, undershooting costs the mark entirely.
 */
export const ASSUMED_PRINTER_MARGIN_MM = 5;
/** Millimetres between glyphs along an edge. */
export const GLYPH_SPACING_MM = 55;
/** Scissors height, across the offcut strip. */
export const GLYPH_SIZE_MM = 3.4;
/** Scissors width at `GLYPH_SIZE_MM` height, keeping the glyph's aspect. */
export const SCISSORS_W_MM = (GLYPH_SIZE_MM * SCISSORS_VIEW_W) / SCISSORS_VIEW_H;
/**
 * Glue-stick height. Larger than the scissors because the outline drawing has
 * much finer strokes — below ~5 mm it stops reading as a glue stick and turns
 * into a few disconnected specks.
 */
export const GLUE_GLYPH_SIZE_MM = 5.6;

/** Ink as [r, g, b, alpha]; line widths and dashes in millimetres. */
export const MARK_STYLE = {
  cutInk: [20, 20, 20, 0.8],
  glueInk: [20, 20, 20, 0.55],
  glueTint: [255, 255, 255, 0.22],
  glueLineWidthMm: 0.25,
  glueDashMm: [1.5, 1.5],
  cutLineWidthMm: 0.3,
  cutDashMm: [2.5, 2],
  glueGlyphOpacity: 0.9,
} as const;

/**
 * Where the marks of one sheet go. Cut lines span the whole sheet; glue areas
 * never reach into a strip that gets cut away.
 */
export function sheetMarkGeometry(
  marks: PageSheetMarks,
  wMm: number,
  hMm: number,
): SheetMarkGeometry {
  const geometry: SheetMarkGeometry = {
    glueTint: [],
    glueLines: [],
    glueGlyphs: [],
    cutLines: [],
    scissors: [],
  };
  const { overlapMm } = marks;
  if (overlapMm <= 0) return geometry;
  const hasCut = marks.cutLeft || marks.cutTop;
  const hasGlue = marks.glueRight || marks.glueBottom;
  if (!hasCut && !hasGlue) return geometry;

  // What the upper sheet ends up covering — the glue area — is whatever the
  // overlap has left after its own trim.
  const trimMm = Math.min(marks.trimMm, overlapMm);
  const glueMm = Math.max(0, overlapMm - trimMm);
  const wasteLeftMm = marks.cutLeft ? trimMm : 0;
  const wasteTopMm = marks.cutTop ? trimMm : 0;

  if (glueMm > 0 && hasGlue) {
    const glueLeftMm = wMm - glueMm;
    const glueTopMm = hMm - glueMm;
    if (marks.glueRight) {
      geometry.glueTint.push({
        xMm: glueLeftMm,
        yMm: wasteTopMm,
        wMm: glueMm,
        hMm: hMm - wasteTopMm,
      });
      geometry.glueLines.push({ x1Mm: glueLeftMm, y1Mm: wasteTopMm, x2Mm: glueLeftMm, y2Mm: hMm });
    }
    if (marks.glueBottom) {
      // The corner already belongs to the right-hand strip.
      const endMm = marks.glueRight ? glueLeftMm : wMm;
      geometry.glueTint.push({
        xMm: wasteLeftMm,
        yMm: glueTopMm,
        wMm: endMm - wasteLeftMm,
        hMm: glueMm,
      });
      geometry.glueLines.push({ x1Mm: wasteLeftMm, y1Mm: glueTopMm, x2Mm: wMm, y2Mm: glueTopMm });
    }

    // Glyphs stay inside the band the printer can actually reach, otherwise
    // they are trimmed off with the edge and the area reads as unexplained.
    const glyphInsetMm = (glueMm + Math.min(glueMm, ASSUMED_PRINTER_MARGIN_MM)) / 2;
    if (marks.glueRight) {
      for (let posMm = wasteTopMm + GLYPH_SPACING_MM / 2; posMm < hMm; posMm += GLYPH_SPACING_MM) {
        geometry.glueGlyphs.push({
          cxMm: wMm - glyphInsetMm,
          cyMm: posMm,
          wMm: GLUE_GLYPH_SIZE_MM,
          hMm: GLUE_GLYPH_SIZE_MM,
          vertical: true,
        });
      }
    }
    if (marks.glueBottom) {
      for (let posMm = wasteLeftMm + GLYPH_SPACING_MM / 2; posMm < wMm; posMm += GLYPH_SPACING_MM) {
        geometry.glueGlyphs.push({
          cxMm: posMm,
          cyMm: hMm - glyphInsetMm,
          wMm: GLUE_GLYPH_SIZE_MM,
          hMm: GLUE_GLYPH_SIZE_MM,
          vertical: false,
        });
      }
    }
  }

  // Scissors sit in the offcut but inside the printable area — between the
  // assumed border and the cut line.
  const cutGlyphMm = (Math.min(ASSUMED_PRINTER_MARGIN_MM, trimMm) + trimMm) / 2;
  if (marks.cutLeft) {
    geometry.cutLines.push({ x1Mm: trimMm, y1Mm: 0, x2Mm: trimMm, y2Mm: hMm });
    for (let posMm = GLYPH_SPACING_MM / 2; posMm < hMm; posMm += GLYPH_SPACING_MM) {
      geometry.scissors.push({
        cxMm: cutGlyphMm,
        cyMm: posMm,
        wMm: SCISSORS_W_MM,
        hMm: GLYPH_SIZE_MM,
        vertical: true,
      });
    }
  }
  if (marks.cutTop) {
    geometry.cutLines.push({ x1Mm: 0, y1Mm: trimMm, x2Mm: wMm, y2Mm: trimMm });
    for (let posMm = GLYPH_SPACING_MM / 2; posMm < wMm; posMm += GLYPH_SPACING_MM) {
      geometry.scissors.push({
        cxMm: posMm,
        cyMm: cutGlyphMm,
        wMm: SCISSORS_W_MM,
        hMm: GLYPH_SIZE_MM,
        vertical: false,
      });
    }
  }

  return geometry;
}
